import { listingQuery, listingReviewQuery } from "@/api/listings";
import { queryClient } from "@/api/utils";
import { ListingEditor } from "@/components/listing-editor/ListingEditor";
import { ListingEditorActions } from "@/components/listing-editor/ListingEditorActions";
import { ListingEditorProvider } from "@/components/listing-editor/ListingEditorProvider";
import { ListingStatusBadge } from "@/components/listings/ListingStatusBadge";
import { Alert } from "@/components/ui/Alert";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { seo } from "@/utils/seo";
import { Flex } from "@givenwell/components";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/supplier/$supplierId/eap/$listingId/$version")({
  component: Component,
  beforeLoad: () => ({
    breadcrumb: "Edit Listing",
  }),
  parseParams: ({ version, ...params }) => {
    const num = +version;
    if (!isFinite(num)) {
      throw new Error("Invalid listing version");
    }
    return {
      ...params,
      version: Math.round(num),
    };
  },
  stringifyParams: ({ version, ...params }) => ({
    ...params,
    version: version.toString(),
  }),
  loader: async ({ params }) => {
    queryClient.removeQueries(listingQuery(params.listingId, params.version));
    await queryClient.prefetchQuery(listingQuery(params.listingId, params.version));
  },
  head: () => ({
    meta: seo({ title: "Edit EAP Listing" }),
  }),
});

function Component() {
  const { listingId, version } = Route.useParams();

  const listingReview = useQuery(listingReviewQuery(version, listingId));
  const { data: listing } = useSuspenseQuery(listingQuery(listingId, version));

  return (
    <ListingEditorProvider key={listing.id + listing.version} defaultListing={listing}>
      {listingReview.data && listingReview.data.review_type === "reject" && (
        <Alert status="error">
          Listing does not meet the Listing Guidelines, please review required edit(s) and publish
          listing again:
          <br />– {listingReview.data.reason}
        </Alert>
      )}
      <PageWrapper
        title={
          <Flex css={{ gap: 8, alignItems: "baseline" }}>
            {listing.title}
            <ListingStatusBadge status={listing.status} version={listing.version} />
            {listing.eap_available && (
              <ListingStatusBadge status="declined" version={listing.version} text="EAP" />
            )}
          </Flex>
        }
        rightContent={<ListingEditorActions />}
      >
        <ListingEditor />
      </PageWrapper>
    </ListingEditorProvider>
  );
}
